import { DatePicker } from 'antd'
import moment from 'moment'
import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useMediaQuery } from 'react-responsive'
import styled from 'styled-components'
import { ActionEnum, getDateFormat, getExpectNumber } from '../../../common'
import {
  ColumnActionHeader,
  ColumnInputHeader,
  ColumnInputRangeHeader,
  NumberFormat,
  PageTitleComponent,
  TableActionColumnButtonComponent,
  TableComponent,
} from '../../../components'
import { translate } from '../../../languages'
import { TransactionEntity } from '../../../models'
import { RootState } from '../../../stores'
import {
  loadTransactions,
  setAction,
  setSelectedTransaction,
  setShowSearchDrawer,
  setTransactionFilter,
  setTransactionsLoaded,
} from '../../../stores/actions'
import {
  ManageTransactionModalComponent,
  SearchTransactionModalComponent,
} from './components'

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
`

const FooterElement = styled.div`
  margin-right: 20px;
  font-weight: bold;
`

export const TransactionPage: React.FC = () => {
  const dispatch = useDispatch()
  const {
    isTransactionsLoaded,
    transactions,
    transactionFilter,
    totalTransactions,
    totalAmountOfTransactions,
    totalVolumeOfTransactions,
  } = useSelector((state: RootState) => state.transaction)
  const { management } = useSelector((state: RootState) => state.common)
  const [filter, setFilter] = React.useState(transactionFilter)
  const { language } = useSelector((state: RootState) => state.language)

  const isDesktopScreen = useMediaQuery({ minWidth: '1300px' })

  React.useEffect(() => {
    if (isDesktopScreen) {
      dispatch(setShowSearchDrawer(false))
    }
  }, [dispatch, isDesktopScreen])

  React.useEffect(() => {
    dispatch(loadTransactions())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  React.useEffect(() => {
    const autoLoad = setInterval(() => {
      dispatch(
        setTransactionFilter({
          ...transactionFilter,
          timeTo: new Date().getTime(),
        })
      )
      dispatch(loadTransactions())
    }, 5000)
    return () => clearInterval(autoLoad)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transactionFilter])

  const columns = React.useMemo(
    () => [
      {
        dataIndex: 'id',
        title: <span>{translate('transaction.id', language)}</span>,
        width: 30,
      },
      {
        title: (
          <>
            <span>{translate('transaction.time', language)}</span>
            <DatePicker.RangePicker
              style={{ width: '100%' }}
              placeholder={[
                translate('common.from', language),
                translate('common.to', language),
              ]}
              format="DD/MM/YYYY HH:mm"
              showTime
              value={[moment(filter.timeFrom), moment(filter.timeTo)]}
              onChange={(e) => {
                setFilter({
                  ...filter,
                  timeFrom:
                    e && e.length === 2 && e[0]
                      ? e[0].toDate().getTime()
                      : filter.timeFrom,
                  timeTo:
                    e && e.length === 2 && e[1]
                      ? e[1].toDate().getTime()
                      : filter.timeTo,
                })
              }}
            />
          </>
        ),
        width: 170,
        render: (_: any, transaction: TransactionEntity) =>
          getDateFormat(transaction.time, true),
      },
      {
        title: (
          <ColumnInputHeader
            titleKey="transaction.station"
            value={filter.stationName}
            onChange={(e) =>
              setFilter({ ...filter, stationName: e.target.value })
            }
          />
        ),
        render: (_: any, transaction: TransactionEntity) =>
          transaction.pumpShift.pump.tank.station.name,
        width: 90,
      },
      {
        title: (
          <ColumnInputHeader
            titleKey="transaction.pump"
            value={filter.pumpName}
            onChange={(e) => setFilter({ ...filter, pumpName: e.target.value })}
          />
        ),
        render: (_: any, transaction: TransactionEntity) =>
          transaction.pumpShift.pump.name,
        width: 80,
      },
      {
        title: (
          <ColumnInputHeader
            titleKey="transaction.shift"
            value={filter.shiftName}
            onChange={(e) =>
              setFilter({ ...filter, shiftName: e.target.value })
            }
          />
        ),
        render: (_: any, transaction: TransactionEntity) =>
          transaction.pumpShift.shift.name,
        width: 90,
      },
      {
        title: (
          <ColumnInputRangeHeader
            titleKey="transaction.volume"
            fromValue={filter.volumeFrom}
            toValue={filter.volumeTo}
            onToChange={(e: any) =>
              setFilter({
                ...filter,
                volumeTo: e.target.value,
              })
            }
            onFromChange={(e: any) =>
              setFilter({
                ...filter,
                volumeFrom: e.target.value,
              })
            }
            fromKey="common.from"
            toKey="common.to"
          />
        ),
        render: (_: any, transaction: TransactionEntity) => (
          <NumberFormat
            value={transaction.volume}
            suffixKey="common.unitType"
          />
        ),
        width: 200,
      },
      {
        title: (
          <ColumnInputRangeHeader
            titleKey="transaction.unitPrice"
            fromValue={filter.unitPriceFrom}
            toValue={filter.unitPriceTo}
            onToChange={(e: any) =>
              setFilter({
                ...filter,
                unitPriceTo: e.target.value,
              })
            }
            onFromChange={(e: any) =>
              setFilter({
                ...filter,
                unitPriceFrom: e.target.value,
              })
            }
            fromKey="common.from"
            toKey="common.to"
          />
        ),
        render: (_: any, transaction: TransactionEntity) => (
          <NumberFormat
            value={transaction.unitPrice}
            suffixKey="common.moneyType"
          />
        ),
        width: 200,
      },
      {
        title: (
          <ColumnInputRangeHeader
            titleKey="transaction.amount"
            fromValue={filter.amountFrom}
            toValue={filter.amountTo}
            onToChange={(e: any) =>
              setFilter({
                ...filter,
                amountTo: e.target.value,
              })
            }
            onFromChange={(e: any) =>
              setFilter({
                ...filter,
                amountFrom: e.target.value,
              })
            }
            fromKey="common.from"
            toKey="common.to"
          />
        ),
        render: (_: any, transaction: TransactionEntity) => (
          <NumberFormat
            value={transaction.totalAmount}
            suffixKey="common.moneyType"
          />
        ),
        width: 200,
      },
      {
        title: (
          <ColumnActionHeader
            onSearch={() => {
              dispatch(setTransactionsLoaded(false))
              dispatch(
                setTransactionFilter({
                  ...filter,
                  pageIndex: 1,
                  volumeFrom: getExpectNumber(
                    true,
                    filter.volumeFrom,
                    filter.volumeTo
                  ),
                  volumeTo: getExpectNumber(
                    false,
                    filter.volumeFrom,
                    filter.volumeTo
                  ),
                  unitPriceFrom: getExpectNumber(
                    true,
                    filter.unitPriceFrom,
                    filter.unitPriceTo
                  ),
                  unitPriceTo: getExpectNumber(
                    false,
                    filter.unitPriceFrom,
                    filter.unitPriceTo
                  ),
                  amountFrom: getExpectNumber(
                    true,
                    filter.amountFrom,
                    filter.amountTo
                  ),
                  amountTo: getExpectNumber(
                    false,
                    filter.amountFrom,
                    filter.amountTo
                  ),
                })
              )
              dispatch(loadTransactions())
            }}
          />
        ),
        render: (_: any, transaction: TransactionEntity) => (
          <TableActionColumnButtonComponent
            onViewButtonClick={() => {
              dispatch(setSelectedTransaction(transaction))
              dispatch(setAction(ActionEnum.VIEW))
            }}
          />
        ),
        width: 20,
      },
    ],
    [dispatch, filter, language]
  )

  return (
    <div style={{ height: '97.5%' }}>
      {management.showManageModal && <ManageTransactionModalComponent />}
      <PageTitleComponent hasSearchDrawer />
      {management.showSearchDrawer && <SearchTransactionModalComponent />}
      <TableComponent
        paginationConfig={{
          current: transactionFilter.pageIndex,
          total: totalTransactions,
        }}
        loading={!isTransactionsLoaded}
        columns={columns}
        data={transactions}
        onChange={(pagination) => {
          const pageIndex = pagination.current || filter.pageIndex
          setFilter({ ...filter, pageIndex })
          dispatch(setTransactionsLoaded(false))
          dispatch(
            setTransactionFilter({
              ...filter,
              pageIndex,
            })
          )
          dispatch(loadTransactions())
        }}
        footer={() => (
          <Footer>
            <FooterElement>
              {`${translate('transaction.totalVolume', language)}: `}
              <NumberFormat
                value={totalVolumeOfTransactions}
                suffixKey="common.unitType"
              />
            </FooterElement>
            <FooterElement>
              {`${translate('transaction.totalAmount', language)}: `}
              <NumberFormat
                value={totalAmountOfTransactions}
                suffixKey="common.moneyType"
              />
            </FooterElement>
          </Footer>
        )}
      />
    </div>
  )
}
